import { PortalManager, Tab, TabList, Tabs, Text } from '@chakra-ui/react';
import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import classNames from 'classnames';
import {
  type CSSProperties,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';

import { FeatureFlag } from '@revelio/core';
import { useUnleashFlag } from '@revelio/core/use-unleash-flag';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Checkbox,
  IconButton,
  InfoTipIcon,
  Input,
  RevelioTooltip,
} from '@revelio/ui';

import { PortalLayer } from '../../../../shared/components/portal-layer/portal-layer';
import {
  CHART_BUILDER_PANEL_Z_INDEX,
  SIDE_PANEL_WIDTH,
} from '../../constants/panelWidths';
import { FilterId } from '../../filters/types/filters';
import { TextContentEditor } from '../Chart/TextChart/TextContentEditor';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { PageType } from '../PageConfigProvider/types';
import type { SecondaryDimensionValues } from '../PageConfigProvider/types/dimension';
import type {
  DimensionSource,
  PositionedItem,
} from '../PageConfigProvider/types/item';
import styles from './ChartBuilderPanel.module.css';
import { ChartCustomizePanel } from './ChartCustomizePanel';
import {
  ChartTypeSelector,
  FIRST_SELECTABLE_CHART_TYPE,
  isUserSelectableChartType,
} from './ChartTypeSelector';
import layoutStyles from './chart-builder-panel.module.css';
import { getChartBuilderPanelVisibility } from './chart-builder-panel-visibility';
import { getEffectiveChartSelectorConfig } from './chart-selector-config';
import { DataFilterSelector } from './DataFilterSelector';
import { DimensionSourceSelector } from './DimensionSourceSelector';
import { MetricSelector } from './MetricSelector';
import { ReportParameterSelector } from './ReportParameterSelector';
import { SecondaryDimensionSelector } from './SecondaryDimensionSelector';
import { SnowflakeParameterList } from './SnowflakeParameterList';
import { SubfilterDimensionSelector } from './SubfilterDimensionSelector';
import labelStyles from './section-label.module.css';
import { TableColumnsEditor } from './TableColumnsEditor';

const CHART_TYPE_SECTION = 'chart-type';

const gaugeFieldClassName = classNames(styles.field, styles.gaugeField);
const titleFieldClassName = classNames(styles.field, styles.titleField);

function getExcludedBreakdownDimensions(
  dimensionSource: DimensionSource | undefined
): SecondaryDimensionValues[] {
  if (!dimensionSource || dimensionSource === 'primary') {
    return [];
  }
  if (dimensionSource === 'competitors') {
    return ['company', 'competitors'];
  }
  return [dimensionSource];
}

const TrashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 17 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M3.1875 4.25L3.89583 15.2292H13.1042L13.8125 4.25"
      stroke="currentColor"
      strokeLinecap="square"
    />
    <path
      d="M6.73047 7.4375V11.6875"
      stroke="currentColor"
      strokeLinecap="square"
    />
    <path
      d="M10.2695 7.4375V11.6875"
      stroke="currentColor"
      strokeLinecap="square"
    />
    <path
      d="M2.125 3.89575H14.875"
      stroke="currentColor"
      strokeLinecap="square"
    />
    <path
      d="M6.05469 3.83703C6.25127 2.66435 7.2711 1.77075 8.49963 1.77075C9.72816 1.77075 10.748 2.66435 10.9446 3.83703"
      stroke="currentColor"
      strokeLinecap="square"
    />
  </svg>
);

type ChartConfigProps = {
  isOpen: boolean;
  item: PositionedItem | undefined;
  selectedIds?: Set<string>;
  clearSelection?: () => void;
  onClose: () => void;
};

export const ChartBuilderPanel = ({
  item,
  isOpen,
  selectedIds,
  clearSelection,
  onClose,
}: ChartConfigProps) => {
  const { dispatch, primaryFilter } = useTabContentConfig();
  const { config: pageConfig } = usePageConfig();
  const isReport = pageConfig.type === PageType.REPORT;
  const customReportsSnowflakeEnabled = useUnleashFlag(
    FeatureFlag.CustomReportsSnowflake
  );

  const id = item?.id;
  const chartType = item?.plot?.chartType;
  const isSelectableChartType = chartType
    ? isUserSelectableChartType(chartType)
    : false;

  const metric = item?.plot?.defaultMetricOptions?.[0];
  const primaryFilterId = primaryFilter?.id;

  const selectorConfig = chartType
    ? getEffectiveChartSelectorConfig(chartType, {
        overrides: item?.plot?.selectorOverrides,
        metric,
        primaryFilterId,
      })
    : null;

  const isSnowflake = item?.plot?.dataSource === 'snowflake';

  const isBarChart = chartType === 'bar';
  const dimensionSource = item?.plot?.dimensionSource;
  const excludedBreakdownDimensions =
    getExcludedBreakdownDimensions(dimensionSource);
  const isStackedBarTimeseries =
    chartType === 'stacked-bar' && dimensionSource === 'timeseries';

  const panelVisibility = getChartBuilderPanelVisibility({
    chartType,
    hasPrimaryFilter: Boolean(primaryFilter),
    isReport,
    customReportsSnowflakeEnabled,
    dataSource: item?.plot?.dataSource,
  });

  const title = item?.title ?? '';
  const isMultiSelect = selectedIds != null && selectedIds.size > 1;

  const handleDeleteChart = () => {
    const idsToDelete = (() => {
      if (selectedIds != null && selectedIds.size > 0) {
        return Array.from(selectedIds);
      }
      if (id !== null && id !== undefined) {
        return [id];
      }
      return [];
    })();
    if (idsToDelete.length === 0) return;
    dispatch({ type: 'DELETE_ITEMS', payload: { ids: idsToDelete } });
    clearSelection?.();
    onClose();
  };

  const handleSubmitTitle = (value: string) => {
    if (id && value !== item?.title) {
      const newTitle = value.trim() === '' ? null : value;
      dispatch({ type: 'UPDATE_ITEM_TITLE', payload: { id, title: newTitle } });
    }
  };

  const [openChartTypeSections, setOpenChartTypeSections] = useState<string[]>(
    []
  );

  // New charts start as the first type before paint (no empty-state flash), picker open.
  useLayoutEffect(() => {
    if (!id || chartType) return;
    setOpenChartTypeSections([CHART_TYPE_SECTION]);
    dispatch({
      type: 'UPDATE_ITEM_CHART_TYPE',
      payload: { id, chartType: FIRST_SELECTABLE_CHART_TYPE },
      replacePreviousHistorySnapshot: true,
    });
  }, [id, chartType, dispatch]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector('[role="listbox"]')) return;
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const [chartTabIndex, setChartTabIndex] = useState(0);

  const renderMetricSelector = () => {
    if (item === undefined) return null;
    if (chartType === 'table' && !isSnowflake) {
      return (
        <TableColumnsEditor item={item} isDisabled={!isSelectableChartType} />
      );
    }
    if (selectorConfig?.metricMode === 'single') {
      return (
        <MetricSelector
          item={item}
          isDisabled={!isSelectableChartType}
          isReport={isReport}
        />
      );
    }
    return null;
  };

  const renderParameterSelector = () => {
    if (item === undefined) return null;
    if (isSnowflake && isReport && customReportsSnowflakeEnabled) {
      return (
        <SnowflakeParameterList
          item={item}
          isDisabled={!isSelectableChartType}
        />
      );
    }
    if (primaryFilter && selectorConfig?.showReportParameters && isReport) {
      return (
        <div className={layoutStyles.fieldSection}>
          <ReportParameterSelector
            item={item}
            parameters={item.plot?.plotParameters ?? []}
            primaryFilter={primaryFilter}
          />
        </div>
      );
    }
    return null;
  };

  const titleField = (
    <>
      <Text
        className={labelStyles.sectionLabel}
        marginBottom="0.5rem"
        fontSize="sm"
        fontWeight="600"
      >
        Title
      </Text>
      <Input
        placeholder="Untitled chart"
        value={title}
        onChange={(e) => handleSubmitTitle(e.target.value)}
        size="sm"
        wrapperClassName={titleFieldClassName}
        readOnly={!isSelectableChartType}
      />
    </>
  );

  const deleteChartButton = (
    <Button
      size="sm"
      fullWidth
      className={styles.deleteChartBtn}
      color="destructive"
      variant="outlined"
      leftIcon={<TrashIcon />}
      onClick={handleDeleteChart}
    >
      Delete chart
    </Button>
  );

  // Portalled at CHART_BUILDER_PANEL_Z_INDEX, below the AI chat (39) and modals (40).
  const panel = (
    <PortalLayer
      style={
        {
          '--portal-layer-z-index': CHART_BUILDER_PANEL_Z_INDEX,
          '--chart-builder-panel-width': SIDE_PANEL_WIDTH,
        } as CSSProperties
      }
    >
      <div className={styles.panel} data-open={isOpen || undefined}>
        <div className={layoutStyles.header}>
          <Text
            className={styles.panelTitle}
            display="flex"
            alignItems="center"
            px={4}
            py={3}
          >
            {isMultiSelect
              ? `${selectedIds?.size ?? 0} charts selected`
              : 'Edit chart'}
          </Text>
          <div className={layoutStyles.headerActions}>
            <span className={styles.escBadge}>Esc</span>
            <IconButton
              aria-label="Close chart builder"
              icon={<IconCrossLarge size={14} className={styles.closeIcon} />}
              size="sm"
              shape="squared"
              variant="ghost"
              onClick={onClose}
            />
          </div>
        </div>

        <div className={styles.headerDivider} />

        {isMultiSelect ? (
          <div className={layoutStyles.deleteChartsSection}>
            <Button
              size="sm"
              fullWidth
              color="destructive"
              variant="outlined"
              leftIcon={<TrashIcon />}
              onClick={handleDeleteChart}
            >
              Delete {selectedIds?.size ?? 0} charts
            </Button>
          </div>
        ) : (
          <>
            <div
              className={classNames(styles.tabsBar, layoutStyles.fixedSection)}
            >
              <Tabs
                isFitted
                size="sm"
                colorScheme="green"
                index={chartTabIndex}
                onChange={(index) => setChartTabIndex(index)}
              >
                <TabList>
                  <Tab fontWeight="600">Setup</Tab>
                  <Tab fontWeight="600">Customize</Tab>
                </TabList>
              </Tabs>
            </div>

            {chartTabIndex === 1 && item ? (
              <div className={layoutStyles.scrollBody}>
                <ChartCustomizePanel
                  key={item.id}
                  item={item}
                  isReport={isReport}
                />
              </div>
            ) : (
              <div className={layoutStyles.scrollBody}>
                {item && (
                  <div className={layoutStyles.titleSection}>{titleField}</div>
                )}

                {!item && isOpen && (
                  <Text fontSize="sm" fontWeight={500}>
                    Select or create a chart on the grid.
                  </Text>
                )}

                {item && (
                  <Accordion
                    value={openChartTypeSections}
                    onChange={setOpenChartTypeSections}
                    className={layoutStyles.chartTypeSection}
                  >
                    <AccordionItem value={CHART_TYPE_SECTION}>
                      <AccordionButton className={layoutStyles.chartTypeToggle}>
                        <div className={layoutStyles.chartTypeLabel}>
                          <Text
                            className={labelStyles.sectionLabel}
                            fontSize="sm"
                            fontWeight="600"
                          >
                            Chart Type
                          </Text>
                          {!!chartType && !isSelectableChartType && (
                            <RevelioTooltip label="This chart's settings are fixed and cannot be changed.">
                              <span>
                                <InfoTipIcon size={16} />
                              </span>
                            </RevelioTooltip>
                          )}
                        </div>
                        <div className={layoutStyles.chartTypeIconSlot}>
                          <AccordionIcon
                            className={layoutStyles.chartTypeChevron}
                          />
                        </div>
                      </AccordionButton>
                      <AccordionPanel className={layoutStyles.chartTypePanel}>
                        <ChartTypeSelector
                          onSelect={() => setOpenChartTypeSections([])}
                          item={item}
                        />
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                )}

                <div className={layoutStyles.sectionBody}>
                  {panelVisibility.showChartOptionBlock && item && (
                    <>
                      {primaryFilter && (
                        <DimensionSourceSelector
                          item={item}
                          primaryFilter={primaryFilter}
                        />
                      )}

                      {renderMetricSelector()}

                      {isSnowflake &&
                        isReport &&
                        customReportsSnowflakeEnabled &&
                        chartType === 'table' &&
                        id && (
                          <Button
                            size="xs"
                            variant="link"
                            className={styles.useStandardMetricsBtn}
                            onClick={() => {
                              dispatch({
                                type: 'UPDATE_ITEM_DATASOURCE',
                                payload: { id, dataSource: 'mw-gql' },
                              });
                            }}
                          >
                            Use standard table metrics
                          </Button>
                        )}

                      {primaryFilter && selectorConfig?.showSegment && (
                        <SecondaryDimensionSelector
                          item={item}
                          primaryFilter={primaryFilter}
                          forceSegment={selectorConfig?.forceSegment ?? false}
                          showGroupBySegment={
                            (selectorConfig?.showGroupBySegment ?? false) &&
                            !isStackedBarTimeseries
                          }
                          isDisabled={!isSelectableChartType}
                          excludeDimensions={
                            isBarChart ? excludedBreakdownDimensions : undefined
                          }
                        />
                      )}

                      {chartType === 'diverging-bar-combo' &&
                        item &&
                        primaryFilterId && (
                          <SubfilterDimensionSelector
                            item={item}
                            primaryFilterId={primaryFilterId}
                            dispatch={dispatch}
                          />
                        )}

                      {primaryFilterId === FilterId.COMPANY &&
                        chartType === 'diverging-bar-combo' &&
                        item &&
                        id && (
                          <div className={layoutStyles.comparisonToggle}>
                            <Checkbox
                              checked={
                                item.plot?.divergingBarCombo
                                  ?.dynamicEntityComparison ?? false
                              }
                              onChange={(e) => {
                                dispatch({
                                  type: 'UPDATE_ITEM_DYNAMIC_ENTITY_COMPARISON',
                                  payload: {
                                    id,
                                    dynamicEntityComparison: e.target.checked,
                                  },
                                });
                              }}
                            >
                              <Text fontSize="sm">
                                Dynamic entity comparison
                              </Text>
                            </Checkbox>
                          </div>
                        )}

                      {selectorConfig?.showFilter && (
                        <DataFilterSelector
                          item={item}
                          plotFilterMenu={
                            selectorConfig.plotFilterMenu ?? 'all'
                          }
                        />
                      )}

                      {renderParameterSelector()}
                    </>
                  )}

                  {chartType === 'text' && id && primaryFilter?.id && item && (
                    <div className={layoutStyles.editorSection}>
                      <TextContentEditor
                        item={item}
                        value={item.plot?.textHtml || ''}
                        onChange={(textHtml) => {
                          dispatch({
                            type: 'UPDATE_ITEM_TEXT_HTML',
                            payload: { id, textHtml },
                          });
                        }}
                        primaryFilterId={primaryFilter.id}
                      />
                      {primaryFilter && isReport && (
                        <div className={layoutStyles.fieldSection}>
                          <ReportParameterSelector
                            item={item}
                            parameters={item.plot?.plotParameters ?? []}
                            primaryFilter={primaryFilter}
                          />
                        </div>
                      )}
                      <div className={layoutStyles.fieldSection}>
                        <Checkbox
                          checked={
                            item.plot?.excludeFromExecutiveSummary ?? false
                          }
                          onChange={(e) => {
                            dispatch({
                              type: 'UPDATE_ITEM_EXCLUDE_FROM_EXECUTIVE_SUMMARY',
                              payload: {
                                id,
                                excludeFromExecutiveSummary: e.target.checked,
                              },
                            });
                          }}
                        >
                          <Text fontSize="sm">
                            Exclude from executive summary
                          </Text>
                        </Checkbox>
                      </div>
                    </div>
                  )}

                  {chartType === 'gauge-v2' && id && item && (
                    <div className={layoutStyles.editorSection}>
                      <Text
                        className={labelStyles.sectionLabel}
                        marginBottom="0.5rem"
                        fontSize="sm"
                        fontWeight="600"
                      >
                        Value
                      </Text>
                      <Input
                        value={item.plot?.gaugeV2?.value ?? ''}
                        onChange={(e) => {
                          dispatch({
                            type: 'UPDATE_ITEM_GAUGE_V2_OPTIONS',
                            payload: { id, value: e.target.value },
                          });
                        }}
                        placeholder='Enter value (0-1) or handlebar template (e.g., {{VALUE "market_tightness"}})'
                        wrapperClassName={gaugeFieldClassName}
                      />
                      <Text
                        className={labelStyles.sectionLabel}
                        marginTop="1rem"
                        marginBottom="0.5rem"
                        fontSize="sm"
                        fontWeight="600"
                      >
                        Min
                      </Text>
                      <Input
                        value={item.plot?.gaugeV2?.min ?? ''}
                        onChange={(e) => {
                          dispatch({
                            type: 'UPDATE_ITEM_GAUGE_V2_OPTIONS',
                            payload: { id, min: e.target.value },
                          });
                        }}
                        placeholder="Optional min (default 0)"
                        wrapperClassName={gaugeFieldClassName}
                      />
                      <Text
                        className={labelStyles.sectionLabel}
                        marginTop="1rem"
                        marginBottom="0.5rem"
                        fontSize="sm"
                        fontWeight="600"
                      >
                        Max
                      </Text>
                      <Input
                        value={item.plot?.gaugeV2?.max ?? ''}
                        onChange={(e) => {
                          dispatch({
                            type: 'UPDATE_ITEM_GAUGE_V2_OPTIONS',
                            payload: { id, max: e.target.value },
                          });
                        }}
                        placeholder="Optional max (default 1)"
                        wrapperClassName={gaugeFieldClassName}
                      />
                      <Text
                        className={labelStyles.sectionLabel}
                        marginTop="1rem"
                        marginBottom="0.5rem"
                        fontSize="sm"
                        fontWeight="600"
                      >
                        Min label
                      </Text>
                      <Input
                        value={item.plot?.gaugeV2?.minLabel ?? ''}
                        onChange={(e) => {
                          dispatch({
                            type: 'UPDATE_ITEM_GAUGE_V2_OPTIONS',
                            payload: { id, minLabel: e.target.value },
                          });
                        }}
                        placeholder="e.g. Favors Employees"
                        wrapperClassName={gaugeFieldClassName}
                      />
                      <Text
                        className={labelStyles.sectionLabel}
                        marginTop="1rem"
                        marginBottom="0.5rem"
                        fontSize="sm"
                        fontWeight="600"
                      >
                        Max label
                      </Text>
                      <Input
                        value={item.plot?.gaugeV2?.maxLabel ?? ''}
                        onChange={(e) => {
                          dispatch({
                            type: 'UPDATE_ITEM_GAUGE_V2_OPTIONS',
                            payload: { id, maxLabel: e.target.value },
                          });
                        }}
                        placeholder="e.g. Favors Employers"
                        wrapperClassName={gaugeFieldClassName}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {item && (
              <div className={layoutStyles.fixedSection}>
                {deleteChartButton}
              </div>
            )}
          </>
        )}
      </div>
    </PortalLayer>
  );

  return (
    <PortalManager zIndex={CHART_BUILDER_PANEL_Z_INDEX}>{panel}</PortalManager>
  );
};
